import { IntlShape } from 'react-intl';
import { TubeStatus_t, tube_t } from 'foreman-slink';
import { JobMessages } from 'src/intl/JobMessages';
import { convertUnit, convertUnitNumber, unitTypeString } from 'src/utils/helpers/chartHelpers';
import { TimeTable, msToTime } from 'src/utils/logs/logHelpers';
import { JobLogV2Data } from 'src/utils/logs/types';
import { TubeSheetGraphBreakpoint, TubeSheetGraphData, TubeSheetGraphDataElement } from './types';
import { TUBE_RADIUS } from './constants';

interface TubeSheetGraphTranslatorConfig {
  intl: IntlShape;
  isMetric: boolean;
  showTimeDetails: boolean;
  timezone: string;
}

/** A collection of utility functions for converting parsed log data into a shape that can be used for drawing the tube sheet graph. */
export class TubeSheetGraphTranslator {
  private readonly intl: IntlShape;
  private readonly isMetric: boolean;
  private readonly showTimeDetails: boolean;
  private readonly timezone: string;
  private readonly dateTimeFormat: Intl.DateTimeFormat;

  constructor(config: TubeSheetGraphTranslatorConfig) {
    this.intl = config.intl;
    this.isMetric = config.isMetric;
    this.showTimeDetails = config.showTimeDetails;
    this.timezone = config.timezone;
    this.dateTimeFormat = new Intl.DateTimeFormat(undefined, {
      timeZone: this.timezone,
      timeZoneName: 'short',
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  public readonly parseDataV2 = (
    tubeSheets: JobLogV2Data['tubeSheet'],
    timeTable: JobLogV2Data['timeTable'],
    autoMove: JobLogV2Data['configuration']['autoMove'],
    firmware: JobLogV2Data['firmwareVersion'] | undefined,
    timeRangeMin: number,
    timeRangeMax: number,
  ): TubeSheetGraphData => {
    const segments: TubeSheetGraphDataElement[][] = [];
    // Determine which tubesheet contains the data we need to visualize.
    // If the job contains one or more resets after at least one tube has been cleaned, there will be multiple
    // tubesheets, one for the reset at the start of the job and then one for each subsequent reset. timeRangeMin and
    // timeRangeMax represent the timeframe over which the tubesheet will be visualized. We really want to display
    // the cumulative tubesheet since the last reset, so we ignore timeRangeMin and use timeRangeMax to find the
    // applicable tubesheet. Default to the last tubesheet in case a tubesheet is not found.
    let lastTubeSheet = tubeSheets[tubeSheets.length - 1];
    for (let i = tubeSheets.length - 1; i >= 0; i--) {
      if (timeRangeMax > tubeSheets[i].msElapsed) {
        lastTubeSheet = tubeSheets[i];
        break;
      }
    }

    if (!lastTubeSheet || timeTable.length === 0) {
      return {
        segments,
        breakpoints: {
          duration: this.getDurationBreakpoints(),
          lanceSpeed: this.getLanceSpeedBreakpoints(),
          depth: this.getDepthBreakpoints(),
        },
      };
    }

    const revision = !firmware || firmware.major < 2 ? 1 : 2;
    const time = TimeTable.from(timeTable);
    const config: TubeSheetElementTranslatorConfig = {
      intl: this.intl,
      isMetric: this.isMetric,
      showTimeDetails: this.showTimeDetails,
      timeTable: time,
      autoMove,
      timeRangeMin,
      timeRangeMax,
      timezone: this.timezone,
      dateTimeFormat: this.dateTimeFormat,
      revision,
    };

    const segment: TubeSheetGraphDataElement[] = [];
    for (const row in lastTubeSheet.tubes) {
      for (const col in lastTubeSheet.tubes[row]) {
        const tube = lastTubeSheet.tubes[row][col];
        const elementTranslator = new TubeSheetElementTranslator(tube, config);

        segment.push(elementTranslator.toElement());
      }
    }
    segments.push(segment);

    const allElements = segments.flat();

    const durationMean =
      allElements.length === 0 ? 0 : allElements.reduce((acc, val) => acc + val.cleanTime, 0) / allElements.length;
    const lanceSpeedMean =
      allElements.length === 0 ? 0 : allElements.reduce((acc, val) => acc + val.lanceSpeed, 0) / allElements.length;
    const depthMean =
      allElements.length === 0 ? 0 : allElements.reduce((acc, val) => acc + val.depth, 0) / allElements.length;

    return {
      segments,
      breakpoints: {
        duration: this.getDurationBreakpoints(durationMean),
        lanceSpeed: this.getLanceSpeedBreakpoints(lanceSpeedMean),
        depth: this.getDepthBreakpoints(depthMean),
      },
    };
  };

  private getBreakpoints(avg: number, warningFactor: number, dangerFactor: number): TubeSheetGraphBreakpoint {
    return { avg, warning: avg * warningFactor, danger: avg * dangerFactor };
  }

  private getDurationBreakpoints(avg?: number) {
    const value = avg || 90000;
    return this.getBreakpoints(value, 2, 3);
  }

  private getLanceSpeedBreakpoints(avg?: number) {
    const value = avg || (this.isMetric ? 0.6096 : 2);
    return this.getBreakpoints(value, 1.5, 2);
  }

  private getDepthBreakpoints(avg?: number) {
    const value = avg || (this.isMetric ? 0.6096 : 2);
    return this.getBreakpoints(value, 1.5, 2);
  }
}

interface TubeSheetElementTranslatorConfig extends TubeSheetGraphTranslatorConfig {
  timeTable: TimeTable;
  autoMove: JobLogV2Data['configuration']['autoMove'];
  timeRangeMin: number;
  timeRangeMax: number;
  dateTimeFormat: Intl.DateTimeFormat;
  revision: number;
}

class TubeSheetElementTranslator {
  private readonly tube: tube_t;
  private readonly intl: IntlShape;
  private readonly isMetric: boolean;
  private readonly showTimeDetails: boolean;
  private readonly timeTable: TimeTable;
  private readonly autoMove: JobLogV2Data['configuration']['autoMove'];
  private readonly timeRangeMin: number;
  private readonly timeRangeMax: number;
  private readonly dateTimeFormat: Intl.DateTimeFormat;
  private readonly revision: number;

  private _statusInfo: tube_t['statusInfo'] | undefined;
  private _eventInfo: tube_t['eventInfo'] | undefined;
  private _astInfo: tube_t['astInfo'] | undefined;

  constructor(tube: tube_t, config: TubeSheetElementTranslatorConfig) {
    this.tube = tube;
    this.intl = config.intl;
    this.isMetric = config.isMetric;
    this.showTimeDetails = config.showTimeDetails;
    this.timeTable = config.timeTable;
    this.autoMove = config.autoMove;
    this.timeRangeMin = config.timeRangeMin;
    this.timeRangeMax = config.timeRangeMax;
    this.dateTimeFormat = config.dateTimeFormat;
    this.revision = config.revision;
  }

  /**
   * Filter the statusInfo array down to whatever is between this.timeRangeMin and this.timeRangeMax.
   *
   * Tubes not in this time range will be marked with the Unknown status.
   *
   * statusInfo is an array to capture the state of the tubes over time to support playback and the playback sliders.
   * Initially, all tubes are set to be uncleaned, meaning the statusInfo arrays each have one element to express this.
   * When a tube is cleaned, another element will be added to the statusInfo array to mark that tube as cleaned.
   * Since statusInfo includes a millisecond offset from the start of the job (msElapsed), elements can be filtered
   * out given a min and max millisecond offset.
   *
   * @private
   */
  private get statusInfo() {
    // This function is called multiple times per tube and the first time it is called we stash the statusInfo that
    // was derived. After that, the stashed value is returned.
    if (this._statusInfo) return this._statusInfo;

    // For the given tube, filter out any element in the statusInfo array that is outside the time range
    const result = !this.timeRangeMax
      ? this.tube.statusInfo
      : this.tube.statusInfo.filter(e => e.msElapsed > this.timeRangeMin && e.msElapsed <= this.timeRangeMax);

    // Stash the result, so it can be returned the next time this function is called.
    this._statusInfo = result;
    return result;
  }

  private get status(): TubeStatus_t {
    if (this.revision < 2) {
      // For backwards compatibility with old firmware versions.
      return this.eventInfo.some(i => i.cleaned) ? TubeStatus_t.Cleaned : TubeStatus_t.Uncleaned;
    }

    if (!this.statusInfo.length) return TubeStatus_t.Unknown;
    return this.statusInfo[this.statusInfo.length - 1].status;
  }

  private get eventInfo() {
    if (this._eventInfo) return this._eventInfo;
    const result = !this.timeRangeMax
      ? this.tube.eventInfo
      : this.tube.eventInfo.filter(
          event => event.msElapsed >= this.timeRangeMin && event.msElapsed <= this.timeRangeMax,
        );
    this._eventInfo = result;
    return result;
  }

  private get astInfo() {
    if (this._astInfo) return this._astInfo;
    const result = !this.timeRangeMax
      ? this.tube.astInfo
      : this.tube.astInfo.filter(ast => ast.msElapsed >= this.timeRangeMin && ast.msElapsed <= this.timeRangeMax);
    this._astInfo = result;
    return result;
  }

  private get depth() {
    return convertUnitNumber(this.tube.depth, 'lanceDepth', this.isMetric);
  }

  private get lanceSpeed() {
    const avgLanceSpeed = this.eventInfo.reduce((prev, item) => {
      const timeIn = item.timeIn / 1000; // ms -> seconds
      const timeOut = item.timeOut / 1000; // ms -> seconds

      if (timeIn > 0 && timeOut > 0) return prev + (item.depth / timeIn + item.depth / timeOut) / 2;
      if (timeIn > 0) return prev + item.depth / timeIn;
      if (item.timeOut > 0) return prev + item.depth / timeOut;
      return prev;
    }, 0);
    return convertUnitNumber(avgLanceSpeed, 'lanceSpeed', this.isMetric);
  }

  private get cleanTime() {
    return this.eventInfo.reduce((total, i) => total + i.timeIn + i.timeOut, 0);
  }

  private get autoStroke() {
    return this.astInfo.length > 0;
  }

  private get depthDescription() {
    return this.intl.formatMessage(JobMessages.tubeSheetDepthTooltip, {
      value: convertUnit(this.tube.depth, 'lanceDepth', this.isMetric),
      unit: unitTypeString('lanceDepth', this.isMetric),
    });
  }

  private get autoStrokeDescription() {
    if (!this.autoStroke) return undefined;

    return this.astInfo.map((ast, index) => {
      const calendarTime = this.timeTable.getInstant(ast.msElapsed);
      const tempAutoStroke = `${convertUnit(ast.depth, 'lanceDepth', this.isMetric)} ${unitTypeString(
        'lanceDepth',
        this.isMetric,
      )}`;

      const autoStrokeInfo = this.showTimeDetails
        ? `${tempAutoStroke}, ${this.dateTimeFormat.format(new Date(calendarTime))}`
        : tempAutoStroke;

      // First entry gets auto stroke label, remainder are displayed on new lines.
      return index === 0
        ? this.intl.formatMessage(JobMessages.tubeSheetAutoStrokeTooltip, { value: autoStrokeInfo })
        : autoStrokeInfo;
    });
  }

  private get durationDescription() {
    if (!this.showTimeDetails) return undefined;
    return msToTime(this.cleanTime, this.intl.formatNumber);
  }

  private get lanceSpeedDescription() {
    return this.intl.formatMessage(JobMessages.tubeSheetLanceSpeedTooltip, {
      value: (this.lanceSpeed ?? 0).toFixed(3),
      unit: unitTypeString('lanceSpeed', this.isMetric),
    });
  }

  public readonly toElement = (): TubeSheetGraphDataElement => {
    const tubeData: TubeSheetGraphDataElement = {
      status: this.status,
      x: this.tube.position.x,
      y: this.tube.position.y * -1, // Invert vertically
      autoStroke: this.autoStroke,
      cleanTime: this.cleanTime,
      radius: TUBE_RADIUS,
      depth: this.depth,
      lanceSpeed: this.lanceSpeed,
      description: {
        depth: this.depthDescription,
        autoStroke: this.autoStrokeDescription,
        duration: this.durationDescription,
        lanceSpeed: this.lanceSpeedDescription,
      },
    };
    return tubeData;
  };
}
